import React, { useState, useRef, useEffect } from "react";
import { Info } from "lucide-react";

/**
 * Minimal tooltip - shows on hover (desktop) + click (touch).
 * Positioned below-right by default; crosses over Details panel.
 *
 * Props:
 *   content - React node to render inside the popover
 *   icon    - optional icon (defaults to the (i) info icon)
 *   testId  - optional data-testid
 */
export default function Tooltip({ content, icon = null, testId = "tooltip" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <span className="relative inline-flex" ref={ref}>
      <button type="button"
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center text-gray-400 hover:text-gray-700 focus:outline-none"
        aria-label="More info"
        data-testid={`${testId}-trigger`}>
        {icon || <Info size={14}/>}
      </button>
      {open && (
        <div
          className="absolute z-50 top-full mt-2 right-0 w-72 max-w-[calc(100vw-2rem)] bg-gray-900 text-white text-xs rounded-lg shadow-lg p-3 leading-relaxed"
          role="tooltip"
          data-testid={`${testId}-content`}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}>
          {content}
        </div>
      )}
    </span>
  );
}
